import type { ParsedInvoiceLine } from "./types";

export const CUSTOM_INTERPRETED_UNIT = "__custom__";

export const INTERPRETED_UNIT_PRESETS: { value: string; label: string }[] = [
  { value: "full pan", label: "Full pan" },
  { value: "half pan", label: "Half pan" },
  { value: "dozen", label: "Dozen" },
  { value: "piece", label: "Piece" },
  { value: "order", label: "Order" },
  { value: "platter", label: "Platter" },
  { value: "station", label: "Station" },
  { value: "service hour", label: "Service hour" },
  { value: "staff hour", label: "Staff hour" },
  { value: "rental quantity", label: "Rental quantity" },
  { value: "equipment count", label: "Equipment count" },
  { value: "commercial line", label: "Commercial line" },
  { value: CUSTOM_INTERPRETED_UNIT, label: "Custom…" },
];

const PRESET_VALUES = new Set(
  INTERPRETED_UNIT_PRESETS
    .map((entry) => entry.value)
    .filter((value) => value !== CUSTOM_INTERPRETED_UNIT),
);

export function detectedUnitLabel(line: ParsedInvoiceLine): string {
  return line.rawUnitLabel?.trim() || "unclear";
}

export function reviewedUnitLabel(line: ParsedInvoiceLine): string {
  return line.interpretedUnit?.trim() || "";
}

export function unitSelectValue(line: ParsedInvoiceLine): string {
  const reviewed = reviewedUnitLabel(line);
  if (!reviewed) return "";
  if (PRESET_VALUES.has(reviewed)) return reviewed;
  return CUSTOM_INTERPRETED_UNIT;
}

function unitAmbiguityPresent(line: ParsedInvoiceLine): boolean {
  if (line.rawUnitLabel?.trim()) return false;
  const interpreted = line.interpretedUnit?.trim().toLowerCase() ?? "";
  return !interpreted || interpreted.includes("unclear") || interpreted === "unit unclear";
}

function quantityAmbiguityPresent(line: ParsedInvoiceLine): boolean {
  return line.needsReview && line.rawQuantity > 0 && !line.interpretedQuantity;
}

export function applyLineReviewUpdate(
  line: ParsedInvoiceLine,
  patch: Partial<ParsedInvoiceLine>,
): ParsedInvoiceLine {
  const next: ParsedInvoiceLine = { ...line, ...patch };
  let needsReview = line.needsReview;

  if (patch.classification !== undefined && line.needsReview) {
    needsReview = false;
  } else if (patch.classification && patch.classification !== line.classification) {
    needsReview = false;
  }

  if (patch.interpretedUnit !== undefined) {
    const reviewed = patch.interpretedUnit.trim();
    if (reviewed && !reviewed.toLowerCase().includes("unclear")) {
      if (unitAmbiguityPresent(line)) {
        needsReview = false;
      }
    }
  }

  if (patch.interpretedQuantity !== undefined) {
    const reviewedQty = patch.interpretedQuantity;
    if (reviewedQty != null && !Number.isNaN(reviewedQty) && reviewedQty > 0) {
      if (quantityAmbiguityPresent(line) || unitAmbiguityPresent(line)) {
        needsReview = false;
      }
    }
  }

  if (patch.needsReview === false) {
    needsReview = false;
  }

  return { ...next, needsReview };
}

export function showReviewedQuantityField(line: ParsedInvoiceLine): boolean {
  return line.needsReview || line.interpretedQuantity != null;
}
